<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useReveal } from '../composables/useReveal'

const { t } = useI18n()
const { elementRef: sectionRef, isVisible } = useReveal(0.1)

const modules = [
  { key: 'customer', icon: 'M' },
  { key: 'waybill', icon: 'W' },
  { key: 'execution', icon: 'E' },
  { key: 'warehouse', icon: 'S' },
  { key: 'finance', icon: 'F' },
  { key: 'monitor', icon: 'O' }
]
</script>

<template>
  <section id="product" ref="sectionRef" class="section product">
    <div class="container-narrow product__header">
      <div :class="['eyebrow', 'reveal', { visible: isVisible }]">{{ t('product.eyebrow') }}</div>
      <h2 :class="['reveal', 'reveal-delay-1', { visible: isVisible }]">{{ t('product.title') }}</h2>
      <p :class="['reveal', 'reveal-delay-2', { visible: isVisible }]">{{ t('product.subtitle') }}</p>
    </div>

    <div class="container product__grid">
      <article
        v-for="(module, index) in modules"
        :key="module.key"
        :class="['product__card', 'reveal', `reveal-delay-${Math.min(index + 1, 4)}`, { visible: isVisible }]"
      >
        <div class="product__icon" aria-hidden="true">{{ module.icon }}</div>
        <h3>{{ t(`product.modules.${module.key}.title`) }}</h3>
        <p>{{ t(`product.modules.${module.key}.desc`) }}</p>
      </article>
    </div>
  </section>
</template>

<style scoped>
.product {
  background: var(--color-bg);
}

.product__header {
  text-align: center;
  margin-bottom: 5rem;
}

.product__header h2 {
  margin-bottom: 1.25rem;
  color: var(--color-dark);
}

.product__header p {
  max-width: 640px;
  margin-inline: auto;
}

.product__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
}

.product__card {
  padding: 2.5rem;
  border-radius: var(--radius-lg);
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  transition: transform var(--duration-fast) var(--ease-out-expo),
    box-shadow var(--duration-fast) var(--ease-out-expo),
    border-color var(--duration-fast) var(--ease-out-expo);
}

.product__card:hover {
  transform: translateY(-6px);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.08);
  border-color: rgba(13, 115, 119, 0.2);
}

.product__icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  background: var(--color-accent-soft);
  color: var(--color-accent);
  font-family: var(--font-display);
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
}

.product__card h3 {
  margin-bottom: 0.75rem;
  font-size: 1.375rem;
  color: var(--color-dark);
}

.product__card p {
  font-size: 0.9375rem;
  line-height: 1.7;
}

@media (max-width: 1024px) {
  .product__grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .product__grid {
    grid-template-columns: 1fr;
  }
}
</style>
